import { useState, forwardRef, useImperativeHandle, useEffect } from "react"
import { Paperclip } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
} from "@f2e/ui/components/base"
import { SUPPORTED_ATTACHMENT_MIME_TYPES } from "@f2e/shared/constants"
import { TooltipContentPlus } from "@f2e/ui/components/base-plus"
import { cn } from "@f2e/ui/lib/utils"

import { FileAttachmentScope, Workspace } from "@/types"
import {
  useFileUploadApi,
  fileUploadResponseToFileBadgeData,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import { validateFiles, MAX_FILE_SIZE_MB } from "@/lib/fileUtils"
import { useFileUploadDrop } from "@/hooks/useFileUploadDrop"
import {
  getApiErrorData,
  getApiErrorPayloadMessage,
  getUserFacingApiErrorMessage,
} from "@/lib/apiErrors"

import { UploadingFile } from "./FileUploadTypes"
import { FileUploadDialog } from "./FileUploadDialog"
import { DragDropOverlay } from "./DragDropOverlay"

interface FileUploadCollectorProps {
  workspace?: Workspace
  className?: string
  children?: React.ReactNode
  onUploadSuccess?: (fileUrls: string[]) => void
  /** Array of current file badges to display */
  files: UploadingFile[]
  /** Callback triggered on any file state change (add/remove/status update) */
  onStateChange?: (files: UploadingFile[]) => void
  /** Whether this is used in evaluation context (affects storage location) */
  isEvaluationContext?: boolean
  /** Whether to show drag overlay UI */
  showDragOverlay?: boolean
  /** When true, shows minimal overlay without icon and text (useful for small drop zones) */
  minimalOverlay?: boolean
  /** Maximum number of files allowed (default: 10) */
  maxFiles?: number
}

export interface FileUploadCollectorHandle {
  handleFiles: (files: File[]) => Promise<void>
}

/**
 * FileUploadCollector component - Handles file collection and upload management
 *
 * This component is responsible for:
 * 1. Collecting files (through dialog or drag and drop)
 * 2. Validating files (type, duplicates, limits)
 * 3. Managing the upload process
 * 4. Notifying parent of state changes
 *
 * It does NOT handle file badge display - that's handled by FileBadgeList separately.
 */
const FileUploadCollector = forwardRef<
  FileUploadCollectorHandle,
  FileUploadCollectorProps
>(
  (
    {
      workspace,
      className,
      children,
      onUploadSuccess,
      files,
      onStateChange,
      isEvaluationContext = false,
      showDragOverlay = false,
      minimalOverlay = false,
      maxFiles = 10,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { alertToast } = useToast()
    const [show, setShow] = useState(false)

    // Update API hook usage to extract error and data
    const {
      error: fileUploadError,
      data: fileUploadResp,
      fetchFn: uploadFile,
    } = useFileUploadApi()

    // Add useEffect to handle API errors and show toast notifications
    useEffect(() => {
      if (!fileUploadError) return
      console.error("File upload API error:", fileUploadError)

      alertToast({
        title: t("common.fileUpload.error.title"),
        description: getUserFacingApiErrorMessage(
          fileUploadError,
          t,
          fileUploadResp?.message || t("common.fileUpload.error.defaultMessage")
        ),
      })
    }, [fileUploadError, fileUploadResp, alertToast, t])

    // Set up drag and drop functionality if enabled
    const { isDragging, dragHandlers } = useFileUploadDrop({
      fileUploadRef: ref as React.RefObject<FileUploadCollectorHandle>,
      workspace,
      logPrefix: "FileUploadCollector",
      onError: (title, message) =>
        alertToast({
          title: t(title),
          description: message,
        }),
    })

    useImperativeHandle(ref, () => ({
      handleFiles,
    }))

    const handleFiles = async (inputFiles: File[]) => {
      if (!inputFiles || inputFiles.length === 0) {
        return
      }

      console.debug(
        `Starting upload process for ${inputFiles.length} files:`,
        inputFiles.map((f) => f.name).join(", ")
      )

      // Validate files for supported types, duplicates, and size
      const { validFiles, unsupportedFiles, duplicateFiles, oversizedFiles } =
        validateFiles(
          inputFiles,
          files,
          MAX_FILE_SIZE_MB,
          SUPPORTED_ATTACHMENT_MIME_TYPES
        )

      // Check for oversized files first (most likely cause of issues)
      if (oversizedFiles.length > 0) {
        alertToast({
          title: t("common.fileUpload.error.title"),
          description: t("common.fileUpload.error.fileTooLarge", {
            files: oversizedFiles.map((f) => f.name).join(", "),
            limit: MAX_FILE_SIZE_MB,
            defaultValue: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit: {{files}}`,
          }),
        })
        return
      }

      // Check for duplicates
      if (duplicateFiles.length > 0) {
        alertToast({
          title: t("common.fileUpload.duplicate.title"),
          description: t("common.fileUpload.duplicate.description", {
            files: duplicateFiles.map((f) => f.name).join(", "),
          }),
        })
        return
      }

      // Check for unsupported file types
      if (unsupportedFiles.length > 0) {
        alertToast({
          title: t("common.fileUpload.error.title"),
          description: t("common.fileUpload.error.unsupportedType", {
            files: unsupportedFiles.map((f) => f.name).join(", "),
            defaultValue: "Unsupported file types: {{files}}",
          }),
        })
        return
      }

      // Check max files limit
      if (files.length + validFiles.length > maxFiles) {
        alertToast({
          title: t("common.fileUpload.error.title"),
          description: t("common.fileUpload.error.maxFiles", {
            limit: maxFiles,
          }),
        })
        setShow(false)
        return
      }

      // Close the dialog regardless of file validation results
      setShow(false)

      if (validFiles.length === 0) return

      // Create badges for new files
      const newBadges: UploadingFile[] = validFiles.map((file) => ({
        file,
        status: "pending",
        scope: FileAttachmentScope.Persistent,
      }))

      // Update badges state with new pending badges
      // We'll maintain a local copy that we'll update as uploads progress
      let localFileState = [...files, ...newBadges]

      // Notify parent of initial state with pending files
      onStateChange?.(localFileState)

      // Check if workspace is available
      if (!workspace) {
        alertToast({
          title: t("common.fileUpload.error.title"),
          description: "Workspace information is missing. Cannot upload files.",
        })

        // Update badges to error state since we can't upload
        localFileState = localFileState.map((badge) => {
          if (
            newBadges.some(
              (newBadge) =>
                newBadge.file.name === badge.file.name &&
                newBadge.file.size === badge.file.size &&
                newBadge.file.lastModified === badge.file.lastModified
            )
          ) {
            return {
              ...badge,
              status: "error" as const,
              errorMessage: "Missing workspace information",
            }
          }
          return badge
        })

        onStateChange?.(localFileState)
        return
      }

      // Collect successful uploads in a local array
      const successfulUploadIds: string[] = []

      console.debug(`Starting uploads for ${newBadges.length} files...`)

      // Upload files one by one to ensure correct handling
      for (const badge of newBadges) {
        console.debug(`Processing file: ${badge.file.name}`)

        // First, set this badge to uploading status
        localFileState = localFileState.map((b) =>
          b.file.name === badge.file.name &&
          b.file.size === badge.file.size &&
          b.file.lastModified === badge.file.lastModified
            ? { ...b, status: "uploading" as const }
            : b
        )

        // Update parent with the uploading status
        onStateChange?.(localFileState)

        // Prepare form data for upload
        const formData = new FormData()
        formData.append("file", badge.file)
        formData.append(
          "data",
          JSON.stringify({
            workspace_id: workspace?.id || "",
            is_evaluation_context: isEvaluationContext,
          })
        )

        // Perform the upload and get back the UUID if successful
        let uuid: string | null = null

        try {
          // Create a promise that resolves when the response is received
          uuid = await new Promise<string | null>((resolve) => {
            console.debug(`Sending API request for file: ${badge.file.name}`)
            uploadFile({
              body: formData,
              callbackFn: (response) => {
                if (response?.data) {
                  try {
                    // Transform the response
                    const fileBadgeData = fileUploadResponseToFileBadgeData(
                      response.data
                    )

                    // Update local state with success
                    localFileState = localFileState.map((b) =>
                      b.file.name === badge.file.name &&
                      b.file.size === badge.file.size &&
                      b.file.lastModified === badge.file.lastModified
                        ? {
                            ...b,
                            status: "uploaded" as const,
                            uuid: fileBadgeData.uuid,
                            url: fileBadgeData.url,
                          }
                        : b
                    )

                    // Update parent state with updated file
                    onStateChange?.(localFileState)

                    // Resolve with UUID
                    resolve(fileBadgeData.uuid)
                  } catch (error: unknown) {
                    console.error(
                      `Error processing response for ${badge.file.name}:`,
                      error
                    )
                    const errorMsg = getApiErrorPayloadMessage(
                      getApiErrorData(error),
                      t,
                      error instanceof Error ? error.message : String(error)
                    )

                    // Update local state with error
                    localFileState = localFileState.map((b) =>
                      b.file.name === badge.file.name &&
                      b.file.size === badge.file.size &&
                      b.file.lastModified === badge.file.lastModified
                        ? {
                            ...b,
                            status: "error" as const,
                            errorMessage: errorMsg || "Upload failed",
                          }
                        : b
                    )

                    // Update parent state with error
                    onStateChange?.(localFileState)
                    resolve(null)
                  }
                } else {
                  console.error(
                    `No data in response for file: ${badge.file.name}`
                  )

                  // Update local state with error
                  localFileState = localFileState.map((b) =>
                    b.file.name === badge.file.name &&
                    b.file.size === badge.file.size &&
                    b.file.lastModified === badge.file.lastModified
                      ? {
                          ...b,
                          status: "error" as const,
                          errorMessage: "No response data received",
                        }
                      : b
                  )

                  // Update parent state with error
                  onStateChange?.(localFileState)
                  resolve(null)
                }
              },
            })
          })
        } catch (error: unknown) {
          console.error(`Error uploading file ${badge.file.name}:`, error)
          const errorMsg = getUserFacingApiErrorMessage(
            error,
            t,
            error instanceof Error ? error.message : String(error)
          )

          // Update local state with error
          localFileState = localFileState.map((b) =>
            b.file.name === badge.file.name &&
            b.file.size === badge.file.size &&
            b.file.lastModified === badge.file.lastModified
              ? {
                  ...b,
                  status: "error" as const,
                  errorMessage: errorMsg || "Upload failed",
                }
              : b
          )

          // Update parent state with error
          onStateChange?.(localFileState)
        }

        if (uuid) {
          successfulUploadIds.push(uuid)
        } else {
          console.error(`Failed to get UUID for file: ${badge.file.name}`)
        }
      }

      console.debug(
        `Upload process completed. Successful uploads: ${successfulUploadIds.length}/${newBadges.length}`
      )

      // Notify parent component of successful uploads
      if (successfulUploadIds.length > 0) {
        onUploadSuccess?.(successfulUploadIds)
      }
    }

    // When showDragOverlay is true, only render drag handlers and overlay
    if (showDragOverlay) {
      return (
        <div
          className={cn("relative transition-all duration-100", className)}
          {...dragHandlers}
        >
          {children}

          {/* Drag overlay */}
          <DragDropOverlay
            isVisible={isDragging}
            className="rounded-lg"
            minimal={minimalOverlay}
          />
        </div>
      )
    }

    // Normal mode: show clickable button/UI
    return (
      <div
        className={cn(
          "flex flex-col relative transition-all duration-300",
          className
        )}
      >
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div onClick={() => setShow(true)} className="cursor-pointer">
                {children || (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContentPlus side="top">
              {t("common.fileUpload.tooltip", "Attach Files")}
            </TooltipContentPlus>
          </Tooltip>

          <FileUploadDialog
            open={show}
            onOpenChange={setShow}
            onFilesSelected={handleFiles}
          />
        </TooltipProvider>
      </div>
    )
  }
)

FileUploadCollector.displayName = "FileUploadCollector"

export { FileUploadCollector }
export default FileUploadCollector
